import { Box, Group, rem, Slider, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconAlertCircle, IconChevronRight, IconChevronUp } from '@tabler/icons-react';
import { useId, useLayoutEffect, useRef } from 'react';
import { IconButton } from 'src/design-system/IconButton';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { THINKING_EFFORT_COPY, THINKING_EFFORTS, ThinkingEffort } from '../chats/thinkingEffort';
import classes from './ThinkingEffortControl.module.css';

type Props = {
  effort: ThinkingEffort;
  onChange: (effort: ThinkingEffort) => void;
  // Owned by the composer, which closes the panel once a message is sent.
  opened: boolean;
  onOpenedChange: (opened: boolean) => void;
};

type EffortAppearance = {
  thumbWidth: number;
  meterBarWidth: number;
  fillFrom: string;
  fillTo: string;
  sheenOpacity: number;
  tradesAwayAccuracy: boolean;
};

// The thumb is the Afternoon logo lozenge: a round "o" at the lowest level, stretching to the full
// lozenge at maximum. The lowest level's grey fill is deliberate, the track only takes brand colour once Siesta is
// actually thinking.
const EFFORT_APPEARANCE: Record<ThinkingEffort, EffortAppearance> = {
  minimal: {
    thumbWidth: 20,
    meterBarWidth: 5,
    fillFrom: 'var(--mantine-color-gray-6)',
    fillTo: 'var(--mantine-color-gray-4)',
    sheenOpacity: 0,
    tradesAwayAccuracy: true,
  },
  quick: {
    thumbWidth: 30,
    meterBarWidth: 7,
    fillFrom: 'var(--mantine-color-brand-8)',
    fillTo: 'var(--mantine-color-brand-5)',
    sheenOpacity: 0.3,
    tradesAwayAccuracy: true,
  },
  balanced: {
    thumbWidth: 40,
    meterBarWidth: 9,
    fillFrom: 'var(--mantine-color-brand-7)',
    fillTo: 'var(--mantine-color-brand-4)',
    sheenOpacity: 0.55,
    tradesAwayAccuracy: false,
  },
  maximum: {
    thumbWidth: 52,
    meterBarWidth: 13,
    fillFrom: 'var(--mantine-color-brand-6)',
    fillTo: 'var(--mantine-color-brand-3)',
    sheenOpacity: 0.9,
    tradesAwayAccuracy: false,
  },
};

const WIDEST_THUMB_WIDTH = EFFORT_APPEARANCE.maximum.thumbWidth;
const LAST_EFFORT_INDEX = THINKING_EFFORTS.length - 1;
const EFFORT_STOPS = THINKING_EFFORTS.map((_, index) => ({ value: index }));

/** The logo mark's shape, drawn on Tabler's grid so it sits level with the alert icon. */
function LozengeIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <rect x={3} y={8.5} width={18} height={7} rx={3.5} />
    </svg>
  );
}

/**
 * How hard Siesta thinks before answering in this chat. Closed, it is a pill above the
 * message text; open, the pill gives way to a panel with a four-stop slider and a caption that
 * spells out what each level below maximum trades away.
 */
export function ThinkingEffortControl({ effort, onChange, opened, onOpenedChange }: Props) {
  const pillRef = useRef<HTMLButtonElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const focusFollowsToggleRef = useRef(false);
  const latestEffortRef = useRef(effort);
  const captionId = useId();

  const effortIndex = THINKING_EFFORTS.indexOf(effort);
  const { label, hint, caption } = THINKING_EFFORT_COPY[effort];
  const appearance = EFFORT_APPEARANCE[effort];
  const captionColour = appearance.tradesAwayAccuracy ? 'brand.2' : 'white';

  // The pill and the panel replace one another, and the one just activated turns inert, which
  // drops keyboard focus onto the page body. A layout effect moves it first.
  useLayoutEffect(() => {
    if (!focusFollowsToggleRef.current) return;
    focusFollowsToggleRef.current = false;
    const sliderThumb = sliderRef.current?.querySelector<HTMLElement>('[role="slider"]');
    (opened ? sliderThumb : pillRef.current)?.focus();
  }, [opened]);

  // A drag reports through the change handler of the render it began in, so a stop is compared
  // with the latest level rather than with that render's.
  useLayoutEffect(() => {
    latestEffortRef.current = effort;
  }, [effort]);

  const toggleOpen = () => {
    focusFollowsToggleRef.current = true;
    onOpenedChange(!opened);
  };

  const selectEffortAt = (index: number) => {
    const next = THINKING_EFFORTS[index];
    if (next !== latestEffortRef.current) onChange(next);
  };

  const pill = (
    <Group justify="flex-end" mb={10}>
      <UnstyledButton
        ref={pillRef}
        className={classes.pill}
        aria-label={`Thinking effort: ${label}`}
        aria-expanded={false}
        onClick={toggleOpen}
      >
        {THINKING_EFFORTS.map((level, index) => {
          const lit = index <= effortIndex;
          return (
            <Box
              key={level}
              component="span"
              className={classes.meterBar}
              mod={{ lit }}
              w={lit ? EFFORT_APPEARANCE[level].meterBarWidth : undefined}
              aria-hidden
            />
          );
        })}
        <Text span fz={12.5} fw={FONT_WEIGHTS.semibold} c="white">
          {label}
        </Text>
        <IconChevronRight size={14} stroke={1.8} className={classes.pillChevron} aria-hidden />
      </UnstyledButton>
    </Group>
  );

  const panel = (
    <Stack className={classes.panel} gap={10} pt={14} px="sm" pb="sm" mb={14}>
      <Group justify="space-between" align="center" gap="xxs" wrap="nowrap">
        <Text size="xs" c="gray.3">
          Effort for this chat
        </Text>
        <Group gap="xxs" align="center" wrap="nowrap">
          <Text fz={13} fw={FONT_WEIGHTS.semibold} c="white">
            {label}{' '}
            <Text span inherit fw={FONT_WEIGHTS.medium} c="gray.3">
              · {hint}
            </Text>
          </Text>
          <IconButton
            size={20}
            className={classes.closeButton}
            aria-label="Close thinking effort"
            aria-expanded
            onClick={toggleOpen}
          >
            <IconChevronUp size={16} stroke={1.8} className={classes.closeIcon} aria-hidden />
          </IconButton>
        </Group>
      </Group>

      <Slider
        ref={sliderRef}
        classNames={{
          root: classes.rail,
          trackContainer: classes.hitArea,
          track: classes.groove,
          bar: classes.fill,
          thumb: classes.thumb,
          markWrapper: classes.tickWrapper,
          mark: classes.tick,
        }}
        styles={{
          bar: { '--effort-fill-from': appearance.fillFrom, '--effort-fill-to': appearance.fillTo },
        }}
        attributes={{ thumb: { 'aria-describedby': captionId } }}
        __vars={{
          '--effort-position': `${(effortIndex / LAST_EFFORT_INDEX) * 100}%`,
          '--effort-thumb-width': rem(appearance.thumbWidth),
          '--effort-widest-thumb': rem(WIDEST_THUMB_WIDTH),
          '--effort-sheen-opacity': String(appearance.sheenOpacity),
        }}
        min={0}
        max={LAST_EFFORT_INDEX}
        marks={EFFORT_STOPS}
        restrictToMarks
        value={effortIndex}
        onChange={selectEffortAt}
        label={null}
        thumbLabel="Thinking effort"
        thumbValueText={label}
        thumbChildren={
          <Box component="span" className={classes.bubble} aria-hidden>
            {label}
          </Box>
        }
      />

      <Group gap="xxs" align="flex-start" wrap="nowrap" mih={34} c={captionColour}>
        <Box component="span" className={classes.captionIcon}>
          {appearance.tradesAwayAccuracy ? (
            <IconAlertCircle size={14} aria-hidden />
          ) : (
            <LozengeIcon />
          )}
        </Box>
        <Text id={captionId} fz={12.5} lh={1.45} c={captionColour}>
          {caption}
        </Text>
      </Group>
    </Stack>
  );

  return (
    <Box className={classes.root}>
      <EffortSlot shown={opened}>{panel}</EffortSlot>
      <EffortSlot shown={!opened}>{pill}</EffortSlot>
    </Box>
  );
}

// Both states stay mounted so one can ease into the other: swapping them in a single frame made
// the composer jump. The hidden one is inert, and its visibility is held by the stylesheet until
// its exit has finished, so it cannot be tabbed to or read out in the meantime.
function EffortSlot({ shown, children }: { shown: boolean; children: React.ReactNode }) {
  return (
    <Box
      className={classes.slot}
      mod={{ shown }}
      inert={!shown}
      style={{ visibility: shown ? 'visible' : 'hidden' }}
    >
      <Box className={classes.slotClip}>
        <Box className={classes.slotContent}>{children}</Box>
      </Box>
    </Box>
  );
}
